import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { getPopularKdramas, getImagesKdramas, getKdramaGenres, getRecentKdrama } from '../components/API';
import KdramaCard from '../components/KdramaCard';
import FilterBar from '../components/FilterBar';
import SearchBar from '../components/SearchBar';
import SearchBarActor from '../components/SearchBarActor';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { UserProvider } from '../context/UserContext';
import { useTheme } from '@mui/system';
import { useMediaQuery } from '@mui/material';
import OtherFilter from '../components/OtherFilter';

function Home() {
    const [kdramas, setKdramas] = useState(JSON.parse(localStorage.getItem('kdramas')) || []);
    const [currentPage, setCurrentPage] = useState(Number(localStorage.getItem('currentPage')) || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState(JSON.parse(localStorage.getItem('filters')) || {});
    const [genres, setGenres] = useState([]);
    const [isRecentKdramas, setIsRecentKdramas] = useState(false); // Pour différencier les K-Dramas récents
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Fonction pour récupérer les K-Dramas populaires avec pagination
    const fetchKdramas = async (page, filters) => {
        try {
            const data = await getPopularKdramas(page, filters);
            const filteredKdramas = data.results.filter(kdrama => kdrama.overview && kdrama.overview.trim() !== '');

            const kdramasWithImages = await Promise.all(
                filteredKdramas.map(async (kdrama) => {
                    const imagesData = await getImagesKdramas(kdrama.id);
                    return {
                        ...kdrama,
                        images: imagesData.backdrops.slice(0, 5),
                        genres: kdrama.genre_ids.map(id => {
                            const genre = genres.find(g => g.id === id);
                            return genre ? genre.name : null;
                        }).filter(Boolean)
                    };
                })
            );

            setKdramas(prevKdramas => {
                const combinedKdramas = [...prevKdramas, ...kdramasWithImages];
                return [...new Map(combinedKdramas.map(kdrama => [kdrama.id, kdrama])).values()];
            });

            setTotalPages(data.total_pages);
            localStorage.setItem('kdramas', JSON.stringify(kdramasWithImages)); // Stocker dans localStorage
        } catch (error) {
            console.error('Erreur lors du chargement des K-dramas populaires:', error);
        }
    };

    // Fonction pour récupérer les K-Dramas récents avec pagination
    const fetchRecentKdramas = async (page = 1, filters) => {
        try {
            const data = await getRecentKdrama(page, filters);
            const filteredKdramas = data.results.filter(kdrama => kdrama.overview && kdrama.overview.trim() !== '');

            const kdramasWithImages = await Promise.all(
                filteredKdramas.map(async (kdrama) => {
                    const imagesData = await getImagesKdramas(kdrama.id);
                    return {
                        ...kdrama,
                        images: imagesData.backdrops.slice(0, 5),
                        genres: kdrama.genre_ids.map(id => {
                            const genre = genres.find(g => g.id === id);
                            return genre ? genre.name : null;
                        }).filter(Boolean)
                    };
                })
            );

            setKdramas(prevKdramas => [...prevKdramas, ...kdramasWithImages]); // Ajouter les K-Dramas récents à la liste
            setTotalPages(data.total_pages);
        } catch (error) {
            console.error('Erreur lors du chargement des K-Dramas récents:', error);
        }
    };

    useEffect(() => {
        const fetchGenres = async () => {
            try {
                const genreData = await getKdramaGenres();
                setGenres(genreData.genres);
            } catch (error) {
                console.error('Erreur lors de la récupération des genres:', error);
            }
        };

        fetchGenres();

        // Restaurer la position de défilement
        const savedScrollPosition = localStorage.getItem('scrollPosition');
        if (savedScrollPosition) {
            window.scrollTo(0, Number(savedScrollPosition));
        }

        return () => {
            localStorage.setItem('scrollPosition', window.scrollY); // Enregistrer la position de défilement
        };
    }, []);

    // Chargement des K-Dramas populaires ou récents selon le mode actuel (récents ou non)
    useEffect(() => {
        if (!isRecentKdramas) {
            fetchKdramas(currentPage, filters);
        } else {
            fetchRecentKdramas(currentPage, filters);
        }
    }, [currentPage, filters, genres, isRecentKdramas]);

    const loadMoreKdramas = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prevPage => prevPage + 1);
            localStorage.setItem('currentPage', currentPage + 1); // Stocker la page courante
        }
    };

    // Gestion des filtres pour les K-Dramas populaires
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setKdramas([]);
        setCurrentPage(1);
        setIsRecentKdramas(false); // Réinitialiser le mode K-Dramas récents
        localStorage.setItem('filters', JSON.stringify(newFilters)); // Stocker les filtres dans localStorage
    };

    // Gestion de la récupération des K-Dramas récents
    const handleRecentKdramasClick = () => {
        setKdramas([]); // Réinitialiser la liste
        setCurrentPage(1);
        setIsRecentKdramas(true); // Activer le mode K-Dramas récents
    };

    return (
        <>
            <UserProvider>
                <Header />
                <Box>
                    <Typography variant="h4" sx={{ marginTop: '2rem', color: 'white', fontWeight: 'bold', textAlign: 'center', marginBottom: '2rem' }}>
                        Découvrez les K-Dramas Populaires
                    </Typography>

                    {/* Barre de filtre toujours active */}
                    <FilterBar onFilterChange={handleFilterChange} />

                    {/* Barre de recherche */}
                    <Box sx={{ display: 'flex', padding: '1rem', justifyContent: 'center', flexDirection: isMobile ? 'column' : 'row', marginTop: '2rem', gap: 1 }}>
                        <SearchBar />
                        <SearchBarActor />
                    </Box>

                    {/* Filtre K-Dramas récents */}
                    <Box sx={{ display: 'flex', padding: '1rem', justifyContent: 'center', flexDirection: isMobile ? 'column' : 'row', }}>
                        <OtherFilter onClick={handleRecentKdramasClick} />
                    </Box>

                    {kdramas.length === 0 ? (
                        <Typography variant="h6" sx={{ textAlign: 'center', marginTop: '2rem' }}>
                            Aucun K-Drama ne correspond à vos critères de recherche.
                        </Typography>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {kdramas.map(kdrama => (
                                    <KdramaCard
                                        key={kdrama.id}
                                        title={kdrama.name}
                                        id={kdrama.id}
                                        posterUrl={`https://image.tmdb.org/t/p/w500${kdrama.poster_path}`}
                                        overview={kdrama.overview}
                                        images={kdrama.images}
                                        year={kdrama.first_air_date}
                                        genres={kdrama.genres}
                                        rating={kdrama.vote_average}
                                    />
                                ))}
                            </Box>
                            {/* Bouton de pagination */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                                <Button variant="contained" onClick={loadMoreKdramas} disabled={currentPage === totalPages}>
                                    Afficher plus
                                </Button>
                            </Box>
                        </>
                    )}
                    <Footer />
                </Box>
            </UserProvider>
        </>
    );
}

export default Home;
